import { action, computed, makeObservable, observable } from 'mobx';

import { devError, devLog } from '../../utils/log';
import type { RootStore } from '../RootStore';

/** What this covers. Crown Rips, Bingo and slots keep using GameUiDialog. */
export type WarmGameKey = 'prizeDrop' | 'trail' | 'hammers' | 'puzzle' | 'sports';

/**
 * Sports launches with `coinType: 'ALL'`, which game-ui discards — its allowlist is GC and SC
 * (`ALLOWED_COIN_TYPES`). So the cold path leaves the guest on its default coin type, and posting the
 * lobby's coin type on the warm path would make warm behave differently from cold.
 */
const KEYS_WITHOUT_COIN_TYPE: ReadonlySet<WarmGameKey> = new Set<WarmGameKey>(['sports']);

export type WarmState = 'idle' | 'warming' | 'ready' | 'live';

/**
 * What a caller hands `launch` to post into the warm document. Structural on purpose: it names only
 * the opt-in flag this store reads, so a full MetagamesCurrentEvent satisfies it and nothing here
 * has to track that type's shape.
 */
export type WarmLaunchPayload = {
  token?: string;
  accessToken?: string;
  /** Sports only: the route game-ui opens inside the warm document (e.g. 'toto'). */
  path?: string;
  metagamesEvent?: {
    gc?: { isOptedIn?: boolean };
    sc?: { isOptedIn?: boolean };
  };
};

/**
 * Owns one metagames-only game-ui iframe, kept booted so opening a metagame costs no boot.
 *
 * Its own iframe, never the slot one — sharing that is what let a metagame launch kill a running slot
 * session in PR #2644. `launch` returns false rather than throwing so callers keep their dialog fallback.
 */
export class MetagamesWarmStore {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);
  }

  @observable
  public state: WarmState = 'idle';

  @observable
  public src: string | null = null;

  @observable
  public activeKey: WarmGameKey | null = null;

  /** Bumped to force React to discard the iframe and mount a fresh one. */
  @observable
  public frameKey = 0;

  /** NOT observable: MobX deep-converts, and converting a cross-origin Window throws SecurityError. */
  private warmWindow: Window | null = null;

  @computed
  public get isVisible(): boolean {
    return this.state === 'live';
  }

  /**
   * Start booting a warm frame. Idempotent.
   *
   * `warm=1` keeps it inert: without it a version=3 document connects both wallet sockets and fetches a
   * profile from its first render, since game-ui seeds `isAuthenticated` from the URL param, not a token.
   */
  @action
  public warm = (): void => {
    if (this.state !== 'idle') {
      return;
    }

    this.src = this.rootStore.gameUiStore.getGameUiURL('', { warm: 1 });
    this.state = 'warming';
  };

  /**
   * game-ui reported it is booted and inert.
   *
   * The window comes from this message, not the iframe's `onLoad`: those are two signals for one
   * condition and can disagree — onLoad can fire before game-ui's listener exists, dropping a launch.
   */
  @action
  public onWarmReady = (win?: Window | null): void => {
    if (win) {
      this.warmWindow = win;
    }

    if (this.state === 'warming') {
      this.state = 'ready';
    }
  };

  /** Posts a launch and reveals the frame. False means "open the dialog instead"; never throws. */
  @action
  public launch = (key: WarmGameKey, type: string, payload: WarmLaunchPayload): boolean => {
    // Refusing while live matters: this document holds a JWT with one gameId baked in, so a second
    // launch would carry a stale credential into another game — silently, not as an error.
    //
    // `closed` matters as much: leaving the lobby unmounts MetagamesWarmFrame and destroys the iframe,
    // but nothing resets this store, so state can still read 'ready' over a detached window. Posting
    // there throws nothing and delivers nothing — the frame would go live and show an empty document.
    if (this.state !== 'ready' || !this.warmWindow || this.warmWindow.closed) {
      // Both paths render a loader, so this branch is otherwise invisible.
      devLog(
        `MetagamesWarmFrame: falling back to the dialog for ${key} — state=${this.state}, window=${this.warmWindow ? (this.warmWindow.closed ? 'closed' : 'yes') : 'no'}`,
      );

      return false;
    }

    // The first open belongs to the pre-message dialog: it is the only surface that shows the event's
    // own artwork, and the only one that opts the player in. Warm-launching straight into the game
    // skips it, and since nothing else opts in, it never gets a later turn either.
    const { metagamesEvent } = payload;
    const eventByCoin = this.rootStore.coinTypeStore.coinType === 'SC' ? metagamesEvent?.sc : metagamesEvent?.gc;

    if (eventByCoin && !eventByCoin.isOptedIn) {
      devLog(`MetagamesWarmFrame: ${key} is not opted in yet, so the pre-message dialog takes this open`);

      return false;
    }

    try {
      if (!KEYS_WITHOUT_COIN_TYPE.has(key)) {
        this.warmWindow.postMessage(
          { type: 'setCoinType', payload: { coinType: this.rootStore.coinTypeStore.coinType } },
          import.meta.env.VITE_GAME_UI_URL,
        );
      }

      this.warmWindow.postMessage({ type, payload }, import.meta.env.VITE_GAME_UI_URL);
    } catch (error) {
      devError('MetagamesWarmStore: posting the launch failed, falling back to the dialog', error);
      return false;
    }

    this.state = 'live';
    this.activeKey = key;
    devLog(`MetagamesWarmFrame: warm-launched ${key} — no boot on this open`);

    return true;
  };

  /**
   * Posts only when the asking game is the one actually running there. Without the key check a message
   * reaches whatever is loaded — how Bingo's auth token was delivered into Trail's iframe last time.
   */
  public postToActiveGame = (key: WarmGameKey, message: unknown): boolean => {
    if (this.activeKey !== key || !this.warmWindow || this.warmWindow.closed) {
      return false;
    }

    try {
      this.warmWindow.postMessage(message, import.meta.env.VITE_GAME_UI_URL);
    } catch (error) {
      devError('MetagamesWarmStore: posting to the active game failed', error);
      return false;
    }

    return true;
  };

  /**
   * Hide the frame and hand it back to idle, keeping it booted, so the next open skips the boot.
   *
   * game-ui unmounts the game's route on goIdle, disposing its state, and clears metagames auth. Not
   * destroyed: mobile and the slot iframe both keep one instance alive forever too.
   */
  @action
  public close = (): void => {
    // Each game's closeGame drives this and can fire twice per exit.
    if (this.state !== 'live' && !this.activeKey) {
      return;
    }

    if (this.warmWindow) {
      try {
        this.warmWindow.postMessage({ type: 'goIdle' }, import.meta.env.VITE_GAME_UI_URL);
      } catch (error) {
        devError('MetagamesWarmStore: goIdle failed, dropping the frame instead', error);
        // Unreachable document: do not claim it is reusable.
        this.warmWindow = null;
        this.activeKey = null;
        this.frameKey += 1;
        this.state = 'idle';
        this.src = null;
        this.warm();

        return;
      }
    }

    this.activeKey = null;
    this.state = this.warmWindow ? 'ready' : 'idle';

    if (this.state === 'idle') {
      this.src = null;
      this.warm();
    }
  };
}
